import React, { useEffect, useRef } from 'react';
import { useAttention } from './AttentionContext';

interface AttentionZoneProps {
  children: React.ReactNode;
  zoneId: string;
  className?: string;
}

/**
 * Invisible wrapper component that tracks viewport visibility time for a content zone.
 * Used for heatmap generation and attention analytics.
 * 
 * Usage:
 * <AttentionZone zoneId="expert-intro">
 *   <ExpertBio />
 * </AttentionZone>
 */
export function AttentionZone({ children, zoneId, className }: AttentionZoneProps) {
  const attention = useAttention();
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!attention || !elementRef.current) return;

    attention.registerZone(zoneId, elementRef.current);

    return () => {
      attention.unregisterZone(zoneId);
    };
  }, [attention, zoneId]);

  // Render children in a div that doesn't affect layout
  return (
    <div ref={elementRef} data-attention-zone={zoneId} className={className}>
      {children}
    </div>
  );
}

export default AttentionZone;
